import { Popover } from '@mui/material';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import CrossIcon from '../../../../../assets/svg/CrossIcon';
import DriversIcon from '../../../../../assets/svg/DriversIcon';
import LayersIcon from '../../../../../assets/svg/LayersIcon';
import Button from '../../../../../react-components/Button';
import { usePlanMapFilters } from '../../hooks/plan-details/usePlanMapFilters';
import { useRetrieveActivePlan } from '../../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../hooks/plan-details/useRetrieveActivePlanDrivers';
import { FiltersPopoverContent } from './FiltersPopoverContent';
import { VehiclesPopoverContent } from './VehiclesPopoverContent';

export const MapFilters = () => {
  const { data: activePlan } = useRetrieveActivePlan();
  const { setMapFilters, mapFilters } = usePlanMapFilters();
  const { data: drivers } = useRetrieveActivePlanDrivers();

  // For vehicles, in case of being less than the max height, calculate it. Otherwise, use the max height
  const VEHICLE_MENU_HEIGHT = Math.min(32 + 42 * (drivers ?? []).length + 20 + 46, 360); // 42 x (vehiculo) + 32 (unassigned) + 20 (padding bloque inferior) + 46 (header)
  const VEHICLE_MENU_WIDTH = 340;

  // Design constants
  const FILTERS_MENU_HEIGHT = 263;
  const FILTERS_MENU_WIDTH = 244;

  const [popoverOpen, setPopoverOpen] = useState<
    | {
        type: `filters` | `zones` | `vehicles`;
        top: number;
        left: number;
      }
    | undefined
  >(undefined);

  if (!activePlan || !drivers) {
    return null;
  }

  return (
    <>
      <Button
        onClick={(event: any) => {
          event.stopPropagation();
          const boundingClient = event.currentTarget.getBoundingClientRect();
          setPopoverOpen({
            type: `filters`,
            top: boundingClient.top - FILTERS_MENU_HEIGHT - 6,
            left: boundingClient.left,
          });
        }}
        variant="flat"
        styleType="secondary"
        style={{
          height: `32px`,
          width: `32px`,
          backgroundColor: RoutalPalette.oldWhite,
          borderRadius: `5px`,
          padding: `0px`,
          border: `none`,
          boxShadow: `0px 0px 8px 4px rgba(125, 154, 150, 0.15)`,
        }}
      >
        <div style={{ display: `flex`, alignItems: `center` }}>
          <LayersIcon width="20px" height="20px" />
        </div>
      </Button>

      {popoverOpen?.type === `filters` ? (
        <Popover
          slotProps={{
            paper: {
              variant: `outlined`,
              style: {
                overflow: `hidden`,
              },
            },
          }}
          anchorReference="anchorPosition"
          anchorPosition={{
            top: popoverOpen.top,
            left: popoverOpen.left,
          }}
          keepMounted
          open={popoverOpen?.type === `filters`}
          onClose={(event: any) => {
            event.preventDefault();
            event.stopPropagation();
            setPopoverOpen(undefined);
          }}
        >
          <div
            style={{
              width: `${FILTERS_MENU_WIDTH}px`,
              height: `${FILTERS_MENU_HEIGHT}px`,
              borderRadius: `10px`,
              boxShadow: `0px 0px 8px 4px rgba(125, 154, 150, 0.15)`,
            }}
          >
            <FiltersPopoverContent />
          </div>
        </Popover>
      ) : null}

      <div style={{ width: `4px` }} />

      <Button
        onClick={(event: any) => {
          event.stopPropagation();
          const boundingClient = event.currentTarget.getBoundingClientRect();
          setPopoverOpen({
            type: `vehicles`,
            top: boundingClient.top - VEHICLE_MENU_HEIGHT - 6,
            left: boundingClient.left,
          });
        }}
        variant="flat"
        styleType="secondary"
        style={{
          height: `32px`,
          width: `32px`,
          borderRadius: `5px`,
          backgroundColor: RoutalPalette.neutral00,
          padding: `0px`,
          border: `none`,
          boxShadow: `0px 0px 8px 4px rgba(125, 154, 150, 0.15)`,
        }}
      >
        <div style={{ display: `flex`, alignItems: `center` }}>
          <DriversIcon width="20px" height="20px" />
        </div>
      </Button>

      {popoverOpen?.type === `vehicles` ? (
        <Popover
          slotProps={{
            paper: {
              variant: `outlined`,
              style: {
                overflow: `auto`,
                borderRadius: `10px`,
              },
            },
          }}
          anchorReference="anchorPosition"
          anchorPosition={{
            top: popoverOpen.top,
            left: popoverOpen.left,
          }}
          keepMounted
          open={popoverOpen?.type === `vehicles`}
          onClose={(event: any) => {
            event.preventDefault();
            event.stopPropagation();
            setPopoverOpen(undefined);
          }}
        >
          <div
            style={{
              width: `${VEHICLE_MENU_WIDTH}px`,
              height: `${VEHICLE_MENU_HEIGHT}px`,
              maxHeight: `360px`,
              overflowY: `auto`,
              boxShadow: `0px 0px 8px 4px rgba(125, 154, 150, 0.15)`,
            }}
          >
            <VehiclesPopoverContent />
          </div>
        </Popover>
      ) : null}

      {!!mapFilters.symbols.tracks && (
        <>
          <div style={{ width: `4px` }} />

          <Button
            onClick={(event: any) => {
              event.stopPropagation();
              setMapFilters({
                ...mapFilters,
                symbols: {
                  ...mapFilters.symbols,
                  tracks: false,
                },
              });
            }}
            variant="flat"
            styleType="secondary"
            style={{
              height: `30px`,
              width: `30px`,
              borderRadius: `5px`,
              backgroundColor: RoutalPalette.primary20n,
              padding: `0px`,
              border: `1px solid ${RoutalPalette.primary20}`,
              boxShadow: `0px 0px 8px 4px rgba(125, 154, 150, 0.15)`,
            }}
          >
            <div style={{ display: `flex`, alignItems: `center` }}>
              <CrossIcon width="20px" height="20px" />
            </div>
          </Button>
        </>
      )}
    </>
  );
};
